import React, { useState, useEffect } from "react";
import { 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  IconButton, 
  CircularProgress,
  Chip,
  Avatar,
  LinearProgress,
  Alert,
  Button,
  Tooltip,
  Divider
} from "@mui/material";
import { 
  People as PeopleIcon, 
  AttachMoney as MoneyIcon, 
  TrendingUp as TrendingIcon,
  AccountBalance as BalanceIcon,
  Visibility as ViewIcon,
  ShoppingCart as CartIcon,
  Refresh as RefreshIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalanceWallet as WalletIcon,
  SwapHoriz as SwapIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon
} from "@mui/icons-material";
import {
  fetchDashboardSummary,
  formatCurrency,
  formatNumber,
  calculatePercentageChange,
  getActivityIcon,
  getStatusColor,
  DashboardSummary,
  DashboardStats,
  RecentTrade,
  RecentActivity,
  TradingStats,
  UserStats
} from "../../Common/SharedApis/Dashboard";
import "./Dashboard.css";

function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardSummary | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchDashboardSummary();
      setDashboardData(data);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch dashboard data');
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchData();
  };

  if (loading) {
    return (
      <Box className="dashboard-loading">
        <CircularProgress size={60} />
        <Typography variant="h6" mt={2}>
          Loading dashboard data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="dashboard-error">
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchData}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!dashboardData) {
    return (
      <Box className="dashboard-error">
        <Alert severity="warning">
          No dashboard data available
        </Alert>
      </Box>
    );
  }

  const { statistics, recentTrades, recentActivities, tradingStats, userStats } = dashboardData;

  // Ensure we have default values if any of these are null/undefined
  const safeStats = statistics || {};
  const safeTradingStats = tradingStats || {};
  const safeUserStats = userStats || {};
  const safeRecentTrades = recentTrades || [];
  const safeRecentActivities = recentActivities || [];

  // Calculate percentage changes (mock data for now - you can implement real calculations)
  const getMockPercentageChange = (value: number) => {
    if (!value || isNaN(value)) return 0;
    const randomChange = (Math.random() - 0.5) * 20;
    return calculatePercentageChange(value, value * (1 - randomChange / 100));
  };

  const stats = [
    {
      title: "Total Users",
      value: formatNumber(safeStats.totalUsers || 0),
      change: getMockPercentageChange(safeStats.totalUsers || 0),
      icon: <PeopleIcon />,
      color: "#667eea",
      bgColor: "rgba(102, 126, 234, 0.1)",
      trend: "up"
    },
    {
      title: "Total Revenue",
      value: formatCurrency(safeStats.totalProfit || 0),
      change: getMockPercentageChange(safeStats.totalProfit || 0),
      icon: <MoneyIcon />,
      color: "#48bb78",
      bgColor: "rgba(72, 187, 120, 0.1)",
      trend: "up"
    },
    {
      title: "Active Trades",
      value: formatNumber(safeStats.activeTrades || 0),
      change: getMockPercentageChange(safeStats.activeTrades || 0),
      icon: <TrendingIcon />,
      color: "#ed8936",
      bgColor: "rgba(237, 137, 54, 0.1)",
      trend: "up"
    },
    {
      title: "Total Balance",
      value: formatCurrency(safeStats.totalBalance || 0),
      change: getMockPercentageChange(safeStats.totalBalance || 0),
      icon: <BalanceIcon />,
      color: "#9f7aea",
      bgColor: "rgba(159, 122, 234, 0.1)",
      trend: "up"
    }
  ];

  const quickStats = [
    {
      title: "Today's Volume",
      value: formatCurrency(safeStats.todayVolume || 0),
      icon: <TimelineIcon />,
      color: "#38b2ac"
    },
    {
      title: "Win Rate",
      value: safeTradingStats.winRate ? `${safeTradingStats.winRate.toFixed(1)}%` : 'N/A',
      icon: <TrendingIcon />,
      color: "#48bb78"
    },
    {
      title: "New Users Today",
      value: formatNumber(safeUserStats.newUsersToday || 0),
      icon: <PeopleIcon />,
      color: "#667eea"
    },
    {
      title: "Pending Deposits",
      value: formatNumber(safeStats.totalDeposits || 0),
      icon: <WalletIcon />,
      color: "#ed8936"
    }
  ];

  return (
    <Box className="dashboard-container">
      {/* Header Section */}
      <Box className="dashboard-header">
        <Box className="header-content">
          <Typography variant="h3" component="h1" className="dashboard-title">
            Dashboard Overview
          </Typography>
          <Typography variant="body1" className="dashboard-subtitle">
            Welcome back! Here's what's happening with your platform today.
          </Typography>
        </Box>
        
        <Box className="header-actions">
          <Typography variant="body2" color="textSecondary" className="last-updated">
            Last updated: {lastUpdated.toLocaleTimeString()}
          </Typography>
          <Tooltip title="Refresh Data">
            <IconButton 
              onClick={handleRefresh} 
              className="refresh-btn"
              disabled={loading}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Main Statistics Cards */}
      <Grid container spacing={3} className="stats-grid">
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card className="stat-card" elevation={0}>
              <CardContent className="stat-card-content">
                <Box className="stat-icon-container" style={{ backgroundColor: stat.bgColor }}>
                  <Box style={{ color: stat.color }}>
                    {stat.icon}
                  </Box>
                </Box>
                <Box className="stat-details">
                  <Typography variant="h4" component="div" className="stat-value">
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" className="stat-title">
                    {stat.title}
                  </Typography>
                  <Box className="stat-change-container">
                    <Typography 
                      variant="body2" 
                      className={`stat-change ${stat.trend}`}
                      style={{ color: stat.color }}
                    >
                      {stat.change}
                    </Typography>
                    <Box className={`trend-indicator ${stat.trend}`}>
                      {stat.trend === 'up' ? <TrendingIcon /> : <TrendingDownIcon />}
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Quick Stats Row */}
      <Grid container spacing={2} className="quick-stats-grid">
        {quickStats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card className="quick-stat-card" elevation={0}>
              <CardContent className="quick-stat-content">
                <Box className="quick-stat-icon" style={{ color: stat.color }}>
                  {stat.icon}
                </Box>
                <Box className="quick-stat-details">
                  <Typography variant="h6" className="quick-stat-value">
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" className="quick-stat-title">
                    {stat.title}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Main Content Grid */}
      <Grid container spacing={3} className="main-content-grid">
        {/* Recent Activities */}
        <Grid item xs={12} lg={8}>
          <Card className="activity-card" elevation={0}>
            <CardContent>
              <Box className="card-header">
                <Typography variant="h6" component="h2" className="card-title">
                  Recent Activities
                </Typography>
                <Button variant="outlined" size="small" className="view-all-btn">
                  View All
                </Button>
              </Box>
              
              <Box className="activity-list">
                {safeRecentActivities && safeRecentActivities.length > 0 ? (
                  safeRecentActivities.slice(0, 8).map((activity, index) => (
                    <Box key={index} className="activity-item">
                      <Box className="activity-icon">
                        <span className="emoji-icon">{getActivityIcon(activity.activityType)}</span>
                      </Box>
                      <Box className="activity-content">
                        <Typography variant="body1" className="activity-action">
                          {activity.description}
                        </Typography>
                        <Box className="activity-meta">
                          <Typography variant="caption" color="textSecondary">
                            {activity.userName} • {new Date(activity.activityDateTime).toLocaleTimeString()}
                          </Typography>
                          <Chip 
                            label={activity.status} 
                            size="small" 
                            className="status-chip"
                            style={{ backgroundColor: getStatusColor(activity.status) + '20', color: getStatusColor(activity.status) }}
                          />
                        </Box>
                      </Box>
                      <Box className="activity-amount">
                        <Typography variant="body2" fontWeight="600">
                          {formatCurrency(activity.amount)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {activity.digitalCoinName}
                        </Typography>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box className="no-data">
                    <Typography variant="body2" color="textSecondary" align="center">
                      No recent activities available
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Trading Performance & Quick Actions */}
        <Grid item xs={12} lg={4}>
          <Grid container spacing={2}>
            {/* Trading Performance */}
            <Grid item xs={12}>
              <Card className="performance-card" elevation={0}>
                <CardContent>
                  <Typography variant="h6" component="h2" className="card-title">
                    Trading Performance
                  </Typography>
                  
                  <Box className="performance-stats">
                    <Box className="performance-item">
                      <Typography variant="body2" color="textSecondary">
                        Win Rate
                      </Typography>
                      <Typography variant="h5" fontWeight="600" color="success.main">
                        {safeTradingStats.winRate ? `${safeTradingStats.winRate.toFixed(1)}%` : 'N/A'}
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={safeTradingStats.winRate || 0} 
                        className="win-rate-progress"
                        sx={{ 
                          backgroundColor: 'rgba(72, 187, 120, 0.2)',
                          '& .MuiLinearProgress-bar': { backgroundColor: '#48bb78' }
                        }}
                      />
                    </Box>
                    
                    <Box className="performance-item">
                      <Typography variant="body2" color="textSecondary">
                        Average Trade Amount
                      </Typography>
                      <Typography variant="h6" fontWeight="600">
                        {formatCurrency(safeTradingStats.averageTradeAmount || 0)}
                      </Typography>
                    </Box>
                    
                    <Box className="performance-item">
                      <Typography variant="body2" color="textSecondary">
                        Total Volume (30 days)
                      </Typography>
                      <Typography variant="h6" fontWeight="600">
                        {formatCurrency(safeTradingStats.thisMonthVolume || 0)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Quick Actions */}
            <Grid item xs={12}>
              <Card className="quick-actions-card" elevation={0}>
                <CardContent>
                  <Typography variant="h6" component="h2" className="card-title">
                    Quick Actions
                  </Typography>
                  <Box className="quick-actions-list">
                    <Tooltip title="View Users">
                      <IconButton className="quick-action-btn" size="large">
                        <PeopleIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View Trades">
                      <IconButton className="quick-action-btn" size="large">
                        <TrendingIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View Deposits">
                      <IconButton className="quick-action-btn" size="large">
                        <MoneyIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View Reports">
                      <IconButton className="quick-action-btn" size="large">
                        <AssessmentIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Recent Trades Table */}
      <Card className="trades-card" elevation={0}>
        <CardContent>
          <Box className="card-header">
            <Typography variant="h6" component="h2" className="card-title">
              Recent Trades
            </Typography>
            <Button variant="outlined" size="small" className="view-all-btn">
              View All Trades
            </Button>
          </Box>
          
          <Box className="trades-table">
            {safeRecentTrades && safeRecentTrades.length > 0 ? (
              safeRecentTrades.slice(0, 5).map((trade, index) => (
              <Box key={index} className="trade-row">
                <Box className="trade-user">
                  <Avatar className="user-avatar">
                    {trade.userName.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box className="user-info">
                    <Typography variant="body2" fontWeight="600">
                      {trade.userName}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {trade.email}
                    </Typography>
                  </Box>
                </Box>
                
                <Box className="trade-details">
                  <Typography variant="body2" fontWeight="600">
                    {trade.digitalCoinName}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Duration: {trade.tradeDuration}s
                  </Typography>
                </Box>
                
                <Box className="trade-amount">
                  <Typography variant="body2" fontWeight="600">
                    {formatCurrency(trade.tradingAmount)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Expected: {formatCurrency(trade.expectedProfit)}
                  </Typography>
                </Box>
                
                <Box className="trade-status">
                  <Chip 
                    label={trade.status} 
                    size="small" 
                    className="status-chip"
                    style={{ backgroundColor: getStatusColor(trade.status) + '20', color: getStatusColor(trade.status) }}
                  />
                  {trade.tradeResult && (
                    <Chip 
                      label={trade.tradeResult} 
                      size="small" 
                      className="result-chip"
                      style={{ 
                        backgroundColor: trade.tradeResult === 'Win' ? 'rgba(72, 187, 120, 0.2)' : 'rgba(245, 101, 101, 0.2)',
                        color: trade.tradeResult === 'Win' ? '#48bb78' : '#f56565'
                      }}
                    />
                  )}
                </Box>
                
                <Box className="trade-time">
                  <Typography variant="caption" color="textSecondary">
                    {new Date(trade.tradingDateTimeUTC).toLocaleDateString()}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {new Date(trade.tradingDateTimeUTC).toLocaleTimeString()}
                  </Typography>
                </Box>
              </Box>
            ))
          ) : (
            <Box className="no-data">
              <Typography variant="body2" color="textSecondary" align="center">
                No recent trades available
              </Typography>
            </Box>
          )}
          </Box>
        </CardContent>
      </Card>

      {/* System Health & Alerts */}
      <Grid container spacing={3} className="system-health-grid">
        <Grid item xs={12} md={6}>
          <Card className="system-health-card" elevation={0}>
            <CardContent>
              <Typography variant="h6" component="h2" className="card-title">
                System Health
              </Typography>
              
              <Box className="health-indicators">
                <Box className="health-item">
                  <Box className="health-label">
                    <Typography variant="body2">API Status</Typography>
                    <Chip label="Healthy" size="small" color="success" />
                  </Box>
                  <LinearProgress variant="determinate" value={100} color="success" />
                </Box>
                
                <Box className="health-item">
                  <Box className="health-label">
                    <Typography variant="body2">Database</Typography>
                    <Chip label="Online" size="small" color="success" />
                  </Box>
                  <LinearProgress variant="determinate" value={95} color="success" />
                </Box>
                
                <Box className="health-item">
                  <Box className="health-label">
                    <Typography variant="body2">Server Load</Typography>
                    <Chip label="Normal" size="small" color="warning" />
                  </Box>
                  <LinearProgress variant="determinate" value={65} color="warning" />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card className="alerts-card" elevation={0}>
            <CardContent>
              <Typography variant="h6" component="h2" className="card-title">
                Recent Alerts
              </Typography>
              
              <Box className="alerts-list">
                <Box className="alert-item">
                  <Box className="alert-icon warning">⚠️</Box>
                  <Box className="alert-content">
                    <Typography variant="body2" fontWeight="600">
                      High withdrawal volume detected
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      2 hours ago
                    </Typography>
                  </Box>
                </Box>
                
                <Box className="alert-item">
                  <Box className="alert-icon info">ℹ️</Box>
                  <Box className="alert-content">
                    <Typography variant="body2" fontWeight="600">
                      New user registration spike
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      4 hours ago
                    </Typography>
                  </Box>
                </Box>
                
                <Box className="alert-item">
                  <Box className="alert-icon success">✅</Box>
                  <Box className="alert-content">
                    <Typography variant="body2" fontWeight="600">
                      System backup completed
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      6 hours ago
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;